import React, { useEffect, useRef, useState } from "react"
import * as style from "./Dropdown.module.scss"

const Chevron = () => (
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="3" fill="none"><path fill="#222" fillRule="evenodd" d="m3.322 2.88404 2.17-1.883.375-.325998c.04176-.033746.07544-.076405.09858-.124852.02314-.048448.03515-.101458.03515-.155148 0-.053689-.01201-.106699-.03515-.155147C5.94244.191447 5.90876.148788 5.867.115042c-.09044-.075743-.20504-.11654563-.323-.11499963H.456C.204.00004237 0 .177042 0 .395042c0 .105.048.206.133.28l.385.333998 2.16 1.875c.09065.07478.20449.11568.322.11568s.23135-.0409.322-.11568Z" clipRule="evenodd" /></svg>
)

const Tick = () => (
  // eslint-disable-next-line max-len
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="11" fill="none"><path fill="#222" fillRule="evenodd" d="m1.72695 4.79198-1.454001 1.373L4.32295 10.45l8.39895-8.75802-1.444-1.383997L4.33395 7.54998l-2.607-2.758Z" clipRule="evenodd" /></svg>
)

const Dropdown = ({
  selectedValue,
  selectedValuePrefixText,
  placeHolder,
  options,
  readOnly,
  isMulti,
  onChange,
  hasFeaturedFirstItem,
  dropdownType,
  customMenuClass,
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const inputRef = useRef()
  const dropdownMenu = useRef()

  useEffect(() => {
    const handler = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setShowMenu(false)
      }
    }

    window.addEventListener('click', handler)
    return () => {
      window.removeEventListener('click', handler)
    }
  })

  const handleInputClick = () => {
    if (readOnly) {
      return
    }

    setShowMenu(!showMenu)
  }

  const handleKeyDown = (e) => {
    if (readOnly) {
      return
    }

    if (e.key === 'Enter') {
      setShowMenu(!showMenu)
    }

    // on Arrow Down
    if (e.keyCode === '40') {
      e.preventDefault()
      if (dropdownMenu && dropdownMenu?.current?.children[0]) {
        dropdownMenu.current.children[0].focus()
      }
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      setShowMenu(false)
    }
  }

  const getDisplay = () => {
    if (!selectedValue || selectedValue.length === 0) {
      return placeHolder
    }
    if (isMulti) {
      return (
        <div className={style.dropdownTags}>
          {selectedValue.map((option, i) => (
            <span key={option} className={style.dropdownTagItem}>
              {option}
              {i !== selectedValue.length - 1
                && <span>,</span>}
            </span>
          ))}
        </div>
      )
    }
    return selectedValuePrefixText ? `${selectedValuePrefixText} ${selectedValue.label}` : selectedValue.label
  }

  const removeOption = (option) => selectedValue.filter((item) => item !== option.value)

  const onItemClick = (option) => {
    const selected = options.filter((item) => item.value === selectedValue)
    let newValue
    if (isMulti) {
      if (selectedValue.findIndex((item) => item === option.value) >= 0) {
        newValue = removeOption(option)
        onChange([option])
      } else {
        newValue = [...selected, option]
        onChange(newValue)
      }
    } else {
      newValue = option
      onChange(newValue)
    }
  }

  const onItemKeyDown = (e, option) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onItemClick(option)
    }

    // on Arrow Down
    if (e.keyCode === '40') {
      if (e.target.nextSibling) {
        e.preventDefault()
        e.target.nextSibling.focus()
      }
    }

    // on Arrow Up
    if (e.keyCode === '38') {
      if (e.target.previousSibling) {
        e.preventDefault()
        e.target.previousSibling.focus()
      }
    }

    // on Tab or Esc
    if (e.keyCode === '9' || e.key === 'Escape') {
      e.preventDefault()
      setShowMenu(false)
    }
  }

  const isSelected = (option) => {
    if (isMulti) {
      const selectedNew = options.filter((item) => selectedValue.find((i) => i === item.value))
      return selectedNew.filter((o) => o.value === option.value).length > 0
    }

    if (!selectedValue) {
      return false
    }

    return selectedValue.value === option.value
  }

  const getOptions = () => options

  return (
    // eslint-disable-next-line max-len
    <div className={`${style.dropdownContainer} ${dropdownType === 'wide' ? 'wide' : ''} ${dropdownType === 'minimal' ? 'minimal' : ''}`}>
      <div
        ref={inputRef}
        tabIndex="0"
        onKeyDown={handleKeyDown}
        onClick={handleInputClick}
        // eslint-disable-next-line max-len
        className={`${style.dropdownInput} ${showMenu || selectedValue.length ? style.dropdownInputActive : ''} ${readOnly ? style.readOnly : ''}`}
        aria-autocomplete="list"
        aria-expanded={showMenu}
      >
        <div className={style.dropdownSelectedValue}>
          {getDisplay()}
          {selectedValue.length >= 3 && <span className={style.counter}>{selectedValue.length}</span>}
        </div>
        <div className={style.dropdownTools}>
          <div className={`${style.dropdownTool} ${showMenu ? style.dropdownToolUp : ''}`}>
            <Chevron />
          </div>
        </div>
      </div>
      {!readOnly && showMenu && (
        <ul
          ref={dropdownMenu}
          // eslint-disable-next-line max-len
          className={`${style.dropdownMenu} ${hasFeaturedFirstItem ? style.dropdownMenuFeatured : ''} ${customMenuClass || ''}`}
          role="listbox"
          aria-multiselectable="true"
        >
          {getOptions().map((option) => (
            <li
              tabIndex="0"
              onKeyDown={(e) => onItemKeyDown(e, option)}
              onClick={() => onItemClick(option)}
              key={option.value}
              value={option.value}
              className={`${style.dropdownItem}`}
              role="option"
              aria-selected={isSelected(option)}
            >
              {isSelected(option)
                && (
                <span className={style.dropdownItemTick}>
                  <Tick />
                </span>
                )}
              {option.content || option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default Dropdown
